<script setup lang="ts">
import {
  useBrowserStore,
  useStatsigEventsStore,
} from '@apps/main/new-desktop/core/stores';
import { i18n } from '@apps/main/modules/localization/localizationSetup';
import { TransportSelector } from '@apps/main/new-desktop/modules/tranposrtSelector';

import { TransportType } from '@libs/trip-planner-interfaces';
import { AppBasePlaceholder, AppFlicker } from '@libs/ui';

import type { ItineraryNoTransportSegment } from '../../models';
import { AppPlaceholderEventType } from '../../models';
import { useTripOverviewPageStore } from '../../stores';

import ItineraryTimeline from './ItineraryTimeline.vue';

const browserStore = useBrowserStore();
const tripOverviewPageStore = useTripOverviewPageStore();
const statsigEventsStore = useStatsigEventsStore();

const { t } = i18n.global;

interface ItineraryNoTransportSegmentProps {
  segment: ItineraryNoTransportSegment;
  isLoading?: boolean;
}

const props = defineProps<ItineraryNoTransportSegmentProps>();

function selectTransport(transport: TransportType): void {
  tripOverviewPageStore.selectTransport(props.segment, transport);
}

function handleAction(actionType: AppPlaceholderEventType): void {
  switch (actionType) {
    case AppPlaceholderEventType.AddFlight:
      selectTransport(TransportType.Flight);
      break;
    case AppPlaceholderEventType.AddFerry:
      selectTransport(TransportType.Ferry);
      break;
  }
}
</script>

<template>
  <ItineraryTimeline :segment-type="props.segment.segmentType">
    <template #banner>
      <AppBasePlaceholder
        class="transport-placeholder"
        :title="
          props.segment.isReturnTransport
            ? t('overview_placeholder.return_transport.title', [
                props.segment.city,
              ])
            : t('overview_placeholder.transport.title', [props.segment.city])
        "
        :description="
          props.segment.isReturnTransport
            ? t('overview_placeholder.return_transport.description')
            : t('overview_placeholder.transport.description')
        "
        :is-small-device="browserStore.isSmallerThan900px"
        :icon-properties="{
          name: 'seatInCircle',
          color: 'color-brand-jungle',
        }"
        :actions="[
          {
            textToDisplay: t('placeholder.add_flight'),
            eventType: AppPlaceholderEventType.AddFlight,
          },
          {
            textToDisplay: t('placeholder.add_ferry'),
            eventType: AppPlaceholderEventType.AddFerry,
          },
        ]"
        :is-loading="props.isLoading"
        @action-clicked="handleAction($event as AppPlaceholderEventType)"
      >
        <template #actions>
          <AppFlicker
            v-if="props.isLoading"
            width="spacing-64"
            :height="
              browserStore.isSmallerThan900px ? 'font-size-s' : 'font-size-m'
            "
            :margin="{ ySpacing: 'spacing-4' }"
          />
          <TransportSelector
            v-else
            :transport-type="TransportType.None"
            :disabled="false"
            :initial-option="t('placeholder.more')"
            :has-user-selected-transport="false"
            :is-upsell-no-transport="true"
            :font-variant="
              browserStore.isSmallerThan900px
                ? 'font-body-s-bold'
                : 'font-body-m-bold'
            "
            simple-transport-options
            @select="tripOverviewPageStore.selectTransport(segment, $event)"
            @open="statsigEventsStore.logTripOverviewTransportSelectorOpened"
          />
        </template>
      </AppBasePlaceholder>
    </template>
  </ItineraryTimeline>
</template>

<style scoped lang="scss">
.transport-placeholder {
  @include for-less-or-equal-than-1199-px {
    margin-left: var(--spacing-8);
  }
  @include for-less-or-equal-than-599-px {
    margin-left: 0;
  }
}
</style>
